import { useCallback } from 'react';

import type { ApiFilestackImageOrVideoDataWithCaption } from 'api/types/ApiFilestackImageData';
import { useI18n } from 'i18n/hooks/useI18n';
import { showModal } from 'zustand-stores/modalStore';

type Args = {
  width?: number;
  height?: number;
  altTextRequired?: boolean;
  values: ApiFilestackImageOrVideoDataWithCaption[] | null | undefined;
  onChange: (data: ApiFilestackImageOrVideoDataWithCaption[]) => void;
  allowSVG?: boolean;
  allowVideo?: boolean;
  extraAnalyticsData?: Record<string, unknown>;
};

export function useMultiImagePickerAddImage({
  width,
  height,
  altTextRequired,
  values,
  onChange,
  allowSVG,
  allowVideo,
  extraAnalyticsData,
}: Args) {
  const { i18n } = useI18n();

  const addImage = useCallback(() => {
    showModal('ADD_OR_EDIT_IMAGE', {
      image: null,
      altText: null,
      caption: null,
      extraAnalyticsData,
      allowSVG: Boolean(allowSVG),
      allowVideo: Boolean(allowVideo),
      altTextRequired: Boolean(altTextRequired),
      imageWidth: width,
      imageHeight: height,
      saveImage: ({ image, altText, caption, videoUrl, type = 'IMAGE' }) => {
        const errors: { altText?: string } = {};

        if (type === 'IMAGE' && !altText && altTextRequired) {
          errors.altText = i18n('Required');
          return errors;
        }

        const newValues = [
          ...(values! || []),
          {
            ...image,
            altText,
            caption,
            videoUrl,
            type,
          } as ApiFilestackImageOrVideoDataWithCaption,
        ] satisfies ApiFilestackImageOrVideoDataWithCaption[];

        onChange(newValues);

        return errors;
      },
    });
  }, [
    i18n,
    width,
    height,
    values,
    altTextRequired,
    allowSVG,
    allowVideo,
    extraAnalyticsData,
    onChange,
  ]);

  return { addImage };
}
